import React, { useEffect, useState } from "react";
import "./LoanDetails.css";
import {
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  OutlinedInput,
  InputAdornment,
} from "@mui/material";
import {
  inputLableStyle,
  formControlStyle,
} from "../../../../calculatorStyleData";

const LoanDetails = ({ onData }) => {
  const [processingFeeOption, setProcessingFeeOption] =
    useState("personalloan");

  // State variables to hold the input values
  const [outstandingLoanAmount, setOutstandingLoanAmount] = useState(500000);
  const [currentInterestRate, setCurrentInterestRate] = useState(12);
  const [currentEMI, setCurrentEMI] = useState(20000);
  const [remainingLoanTenure, setRemainingLoanTenure] = useState(30);
  const [newLoanInterestRate, setNewLoanInterestRate] = useState(15);
  const [newLoanTenure, setNewLoanTenure] = useState(30);
  const [processingFee, setProcessingFee] = useState(4.5);

  const formatter = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0, // Ensure no decimals
      maximumFractionDigits: 0, // Ensure no decimals
    }).format(value);
  };

  const handleCalculate = () => {
    // Step 1: EMI Calculation (New Loan EMI)
    const R = newLoanInterestRate / 100 / 12;
    const N = newLoanTenure;
    const EMI =
      (outstandingLoanAmount * R * Math.pow(1 + R, N)) /
      (Math.pow(1 + R, N) - 1);

    // Step 2: Monthly Savings
    const savings = currentEMI - EMI;

    // Step 3: Total Interest Paid
    const totalEMIPayments = currentEMI * remainingLoanTenure;
    const totalInterestPaidOld = totalEMIPayments - outstandingLoanAmount;

    const totalEMIPaymentsNew = EMI * N;
    const totalInterestPaidNew = totalEMIPaymentsNew - outstandingLoanAmount;

    const totalInterestSaved = totalInterestPaidOld - totalInterestPaidNew;

    // Step 4: Processing Fee
    const processingFeeAmount = (outstandingLoanAmount * processingFee) / 100;

    // Step 5: Net Savings
    const netSavings =
      totalInterestPaidOld - totalInterestPaidNew - processingFeeAmount;

    // Form Data Object
    const formData = {
      outstandingLoanAmount,
      currentInterestRate,
      currentEMI,
      remainingLoanTenure,
      newLoanInterestRate,
      newLoanTenure,
      EMI: isNaN(EMI) ? 0 : formatter(EMI),
      monthlySavings: isNaN(savings) ? 0 : formatter(savings),
      totalInterestSaved: isNaN(totalInterestSaved)
        ? 0
        : formatter(totalInterestSaved),
      processingFeeAmount: isNaN(processingFeeAmount)
        ? 0
        : formatter(processingFeeAmount),
      netSavings: isNaN(netSavings) ? 0 : formatter(netSavings),
    };

    // Pass formData to the parent component
    if (onData) onData(formData);
  };

  useEffect(() => {
    handleCalculate();
  }, [
    outstandingLoanAmount,
    currentInterestRate,
    currentEMI,
    remainingLoanTenure,
    newLoanInterestRate,
    newLoanTenure,
    processingFee,
  ]);

  const formatCurrency = (value) => {
    return value.toLocaleString("en-IN");
  };

  const handleAmountChange = (e, setter) => {
    const value = e.target.value.replace(/,/g, ""); // Remove commas
    if (/^\d*\.?\d*$/.test(value)) {
      // Allow only numbers and a single decimal point
      setter(Number(value));
    }
  };

  // handle lone type radio button-
  const handleProcessingFeeOpion = (event) => {
    setProcessingFeeOption(event.target.value);
  };
  return (
    <>
      <div
        id="loan_details__container"
        className=" loan_details__content__box rounded py-4 px-3"
      >
        <Typography
          sx={{
            fontSize: "16px",
            lineHeight: "24px",

            fontFamily: "'Archivo', sans-serif",
          }}
          className="px-2 fw-bold"
        >
          Your Current Loan
        </Typography>
        <div className="d-flex flex-column gap-1 mt-3">
          <div className="details_box p-2">
            <p style={inputLableStyle} className="mb-1">
              Outstanding Loan Amount (₹):
            </p>
            <FormControl fullWidth sx={formControlStyle}>
              <OutlinedInput
                id="outlined-adornment-amount"
                fullWidth
                size="small"
                startAdornment={
                  <InputAdornment position="start">
                    <span className="archivo">&#8377;</span>
                  </InputAdornment>
                }
                value={formatCurrency(outstandingLoanAmount)} // Display value with commas
                onChange={(e) =>
                  handleAmountChange(e, setOutstandingLoanAmount)
                }
              />
            </FormControl>
          </div>
          <div className="details_box p-2">
            <p style={inputLableStyle} className="mb-1">
              Current Interest Rate (APR %)
            </p>
            <FormControl fullWidth sx={formControlStyle}>
              <OutlinedInput
                id="outlined-adornment-amount"
                fullWidth
                size="small"
                sx={{
                  fontFamily: "'Archivo',sans-serif",
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <span className="archivo">%</span>
                  </InputAdornment>
                }
                value={currentInterestRate || 0}
                onChange={(e) => {
                  let value = e.target.value.replace(/,/g, "");
                  if (/^\d*\.?\d*$/.test(value)) {
                    if (value === "") {
                      setCurrentInterestRate("");
                    } else {
                      setCurrentInterestRate(value.replace(/^0+/, "") || "0");
                    }
                  }
                }}
                onFocus={() =>
                  currentInterestRate === "0" && setCurrentInterestRate("")
                }
                onBlur={() =>
                  setCurrentInterestRate(
                    currentInterestRate === "" ? "0" : currentInterestRate
                  )
                }
              />
            </FormControl>
          </div>
          <div className="details_box  p-2">
            <p style={inputLableStyle} className="mb-1">
              Current EMI(₹):
            </p>
            <FormControl fullWidth sx={formControlStyle}>
              <OutlinedInput
                id="outlined-adornment-amount"
                fullWidth
                size="small"
                startAdornment={
                  <InputAdornment position="start">
                    <span className="archivo">&#8377;</span>
                  </InputAdornment>
                }
                value={formatCurrency(currentEMI)}
                onChange={(e) => handleAmountChange(e, setCurrentEMI)}
              />
            </FormControl>
          </div>
          <div className="details_box p-2">
            <p style={inputLableStyle} className="mb-1">
              Remaining Loan Tenure (Months):
            </p>
            <FormControl fullWidth sx={formControlStyle}>
              <OutlinedInput
                id="outlined-adornment-amount"
                fullWidth
                size="small"
                value={formatCurrency(remainingLoanTenure)} // Display value with commas
                onChange={(e) => handleAmountChange(e, setRemainingLoanTenure)}
              />
            </FormControl>
          </div>
          <div className="px-2 mt-3">
            <div
            // className={`origination_details ${
            //   addOrginationOpen === true ? "d-flex" : "d-none"
            // }  flex-column gap-1 mt-4`}
            >
              <div className="details_box py-2">
                <p style={inputLableStyle} className="mb-1">
                  Transfer Loan Type:
                </p>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  name="radio-buttons-group"
                  className="gap-2"
                  value={processingFeeOption}
                  onChange={handleProcessingFeeOpion}
                >
                  <div
                    style={{
                      width: "100%",
                      border: "1.5px solid #b783cc",
                      padding: "0.1px 2px 0.1px 0",
                      backgroundColor: "#fff",
                      borderRadius: "4px",
                    }}
                  >
                    <FormControlLabel
                      value="personalloan"
                      sx={{ paddingLeft: "13px" }}
                      control={<Radio size="small" />}
                      label={
                        <span className="radio_label text-break archivo">
                          Personal Loan
                        </span>
                      }
                    />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      border: "1.5px solid #b783cc",
                      padding: "0.1px 2px 0.1px 0",
                      backgroundColor: "#fff",
                      borderRadius: "4px",
                    }}
                  >
                    <FormControlLabel
                      value="creditcard"
                      sx={{ paddingLeft: "13px" }}
                      control={<Radio size="small" />}
                      label={
                        <span className="radio_label text-break archivo">
                          Credit Card
                        </span>
                      }
                    />
                  </div>
                </RadioGroup>
              </div>

              <div className="details_box  py-2">
                <div className="pt-3 pb-3">
                  <Typography
                    sx={{
                      fontSize: "16px",
                      lineHeight: "24px",

                      fontFamily: "'Archivo', sans-serif",
                    }}
                    className=" fw-bold"
                  >
                    Your New Loan
                  </Typography>
                </div>
                <div className="new-loan-box pt-2">
                  <p style={inputLableStyle} className="mb-1">
                    Processing Fee (%):
                  </p>
                  <FormControl fullWidth sx={formControlStyle}>
                    <OutlinedInput
                      id="outlined-adornment-amount"
                      fullWidth
                      size="small"
                      endAdornment={
                        <InputAdornment position="end">
                          <span className="archivo">%</span>
                        </InputAdornment>
                      }
                      value={processingFee || 0}
                      onChange={(e) => {
                        let value = e.target.value.replace(/,/g, "");
                        if (/^\d*\.?\d*$/.test(value)) {
                          if (value === "") {
                            setProcessingFee("");
                          } else {
                            setProcessingFee(value.replace(/^0+/, "") || "0");
                          }
                        }
                      }}
                      onFocus={() =>
                        processingFee === "0" && setProcessingFee("")
                      }
                      onBlur={() =>
                        setProcessingFee(
                          processingFee === "" ? "0" : processingFee
                        )
                      }
                    />
                  </FormControl>
                </div>
                <div className="new-loan-box pt-4">
                  <p style={inputLableStyle} className="mb-1">
                    New Loan Tenure (Months):
                  </p>
                  <FormControl fullWidth sx={formControlStyle}>
                    <OutlinedInput
                      id="outlined-adornment-amount"
                      fullWidth
                      size="small"
                      value={formatCurrency(newLoanTenure || 0)}
                      onChange={(e) => handleAmountChange(e, setNewLoanTenure)}
                    />
                  </FormControl>
                </div>
                <div className="new-loan-box pt-4">
                  <p style={inputLableStyle} className="mb-1">
                    New Loan Interest Rate (APR %):
                  </p>
                  <FormControl fullWidth sx={formControlStyle}>
                    <OutlinedInput
                      id="outlined-adornment-amount"
                      fullWidth
                      size="small"
                      endAdornment={
                        <InputAdornment position="end">
                          <span className="archivo">%</span>
                        </InputAdornment>
                      }
                      value={newLoanInterestRate || 0}
                      onChange={(e) => {
                        let value = e.target.value.replace(/,/g, "");
                        if (/^\d*\.?\d*$/.test(value)) {
                          if (value === "") {
                            setNewLoanInterestRate("");
                          } else {
                            setNewLoanInterestRate(
                              value.replace(/^0+/, "") || "0"
                            );
                          }
                        }
                      }}
                      onFocus={() =>
                        newLoanInterestRate === "0" &&
                        setNewLoanInterestRate("")
                      }
                      onBlur={() =>
                        setNewLoanInterestRate(
                          newLoanInterestRate === "" ? "0" : newLoanInterestRate
                        )
                      }
                    />
                  </FormControl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoanDetails;
